/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ViewVirtualEventRegistrationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";

import { ReactComponent as CalendarIcon } from "../../assets/icons/ViewRegistrationModal/CalendarIcon.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/ViewRegistrationModal/ClockIcon.svg";
import { fetchVirtualEventById } from "../../logic/virtualEvents";
import { useNavigate } from "react-router";

import styles from "./index.module.css";

/**
 * Modal: virtual event registration summary (title, date/time, Teams link).
 * Matches {@link ViewRegistrationModal} layout for consistency.
 */
const ViewVirtualEventRegistrationModal = ({ open, onClose, event }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [teamsLink, setTeamsLink] = useState(
    () => event?.enlaceReunionMicrosoftTeams || "",
  );

  useEffect(() => {
    if (!open || !event?._id) {
      return;
    }
    setTeamsLink(event?.enlaceReunionMicrosoftTeams || "");
    let cancelled = false;
    const load = async () => {
      if (event?.enlaceReunionMicrosoftTeams) {
        return;
      }
      const full = await fetchVirtualEventById(event._id, navigate);
      if (!cancelled && full?.enlaceReunionMicrosoftTeams) {
        setTeamsLink(String(full.enlaceReunionMicrosoftTeams));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, event?._id, event?.enlaceReunionMicrosoftTeams, navigate]);

  if (!open || !event) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose} type="button">
          <CloseIcon />
        </button>

        <div className={styles.modalHeader}>
          <h2>{event.nombre}</h2>
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <CalendarIcon className={styles.icon} />
              <span>
                {t("components.viewVirtualEventRegistrationModal.eventDate")}:
              </span>
            </div>
            <div className={styles.infoValue}>
              {dayjs(event.fechaEvento).utc().format("LL")}
            </div>
          </div>

          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <ClockIcon className={styles.icon} />
              <span>
                {t("components.viewVirtualEventRegistrationModal.eventTime")}:
              </span>
            </div>
            <div className={styles.infoValue}>{event.hora || "—"}</div>
          </div>
        </div>

        <div className={styles.linkSection}>
          <div className={styles.linkLabel}>
            {t("components.viewVirtualEventRegistrationModal.virtualLinkLabel")}
          </div>
          {teamsLink ? (
            <a
              className={styles.linkAnchor}
              href={teamsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {teamsLink}
            </a>
          ) : (
            <span className={styles.linkPlaceholder}>
              {t("components.viewVirtualEventRegistrationModal.linkPending")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewVirtualEventRegistrationModal;
